import type { AIEventsInstrumentationConfig } from '@atlassian/ai-analytics/ai-events-instrumentation-context';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics/use-ai-events-instrumentation';
import { useMemo } from 'react';

import { useDevAIWebAnalyticsClient } from '../rovo-dev-web-analytics-events';

/**
 * Public config for dev-agents AI Unified events. Wrapper-owned fields are
 * Omit'd so callers cannot override them.
 */
export type DevAgentsAIAnalyticsConfig = Omit<
	AIEventsInstrumentationConfig,
	'product' | 'customAnalyticsFire' | 'isAIFeature'
> & {
	/** Stable id for cross-event correlation; becomes `singleInstrumentationID`. */
	conversationId?: string;
};

/**
 * Builds an `AIEventsInstrumentationConfig` with dev-agents defaults: pins
 * `product: 'devAi'`, `isAIFeature: 1`, and routes events through the
 * dedicated AI client so the GASv3 envelope's product matches the AI MAU
 * registration. Pass the result to `<AIEventsInstrumentationProvider>`.
 */
export const useDevAgentsAIAnalyticsConfig = (
	config: DevAgentsAIAnalyticsConfig,
): AIEventsInstrumentationConfig => {
	const { fireTrackEvent } = useDevAIWebAnalyticsClient();
	const { conversationId, ...rest } = config;

	return useMemo<AIEventsInstrumentationConfig>(
		() => ({
			product: 'devAi',
			isAIFeature: 1,
			singleInstrumentationID: conversationId,
			...rest,
			// Forwards each AI event through the AI client's `fireTrackEvent`
			// (which inherits orgId + common analytics data). The first arg is
			// an `InternalAIEvent` union — Confluence-style events nest the
			// same fields under `data`.
			customAnalyticsFire: (aiEvent) => {
				const eventPayload =
					'data' in aiEvent ? aiEvent.data : (aiEvent as { [key: string]: unknown });

				const action = eventPayload.action as string;
				const actionSubject = eventPayload.actionSubject as string;
				const source = (eventPayload.source as string | undefined) ?? '';
				const attributes = (eventPayload.attributes ?? {}) as Record<string, unknown>;

				fireTrackEvent({
					action,
					actionSubject,
					source,
					attributes,
				});
			},
		}),
		[fireTrackEvent, conversationId, rest],
	);
};

/**
 * Re-export of `useAIEventsInstrumentation`. Must be called inside an
 * `<AIEventsInstrumentationProvider>` whose config came from
 * `useDevAgentsAIAnalyticsConfig`.
 */
export const useDevAgentsAIAnalytics: typeof useAIEventsInstrumentation =
	useAIEventsInstrumentation;
